import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Archive, VisibilityOutlined } from "@mui/icons-material";
import ShipmentsListModal from "components/Shared/ShipmentsListModal";
import { useLazyGetDdlVendorCustomerPendingShipmentForCollectionPerBranchQuery } from "store/services/collectionApi";
import { useLazyGetBranchWiseUnloadingStatsCountQuery } from "store/services/driverApi";

function ToLoadBranchPanel({ driverId }) {
  const [
    getDdlPendingShipments,
    { data: pendingShipmentsData, isLoading: isPendingShipmentsLoading },
  ] = useLazyGetDdlVendorCustomerPendingShipmentForCollectionPerBranchQuery();

  const [
    getLoadedShipmentsCount,
    { data: loadedShipmentsCountData, isLoading: isLoadedCountLoading },
  ] = useLazyGetBranchWiseUnloadingStatsCountQuery();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedShipments, setSelectedShipments] = useState([]);

  useEffect(() => {
    if (driverId) {
      getDdlPendingShipments({});
      getLoadedShipmentsCount({ driverId }); // Pass driverId to the count API
    }
  }, [driverId, getDdlPendingShipments, getLoadedShipmentsCount]);

  // Calculate total shipments across all branches (for To Load Shipments)
  const totalShipments = pendingShipmentsData?.branches?.reduce(
    (acc, branch) => acc + (branch.shipments?.length || 0),
    0
  ) || 0;

  // Get loaded shipments count from the count API
  const loadedShipmentsCount = loadedShipmentsCountData?.pendingUnloadingCount || 0;

  // Handle branch name click to open modal
  const handleBranchNameClick = (branch) => {
    setSelectedBranch(branch);
    setSelectedShipments(branch.shipments || []);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedBranch(null);
    setSelectedShipments([]);
  };

  return (
    <Grid container spacing={2} mt={2}>
      {/* First Table: To Load Shipments */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4, boxShadow: 2, height: "100%" }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Archive sx={{ color: "secondary.main", fontSize: 28, mr: 1 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              className="text-gray-900 dark:text-white"
            >
              To Load Shipments{" "}
              <Typography
                component="span"
                variant="h6"
                fontWeight="bold"
                sx={{ color: 'primary.main' }}
              >
                ({totalShipments})
              </Typography>
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center" mt={2}>

          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      fontWeight: "bold",
                    }}
                  >
                    Branch Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      fontWeight: "bold",
                    }}
                  >
                    Shipments
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      fontWeight: "bold",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isPendingShipmentsLoading ? (
                  <TableRow key="loading">
                    <TableCell align="center">
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        py={2}
                      >
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        Loading branch data...
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : pendingShipmentsData?.branches?.length ? (
                  pendingShipmentsData.branches.map((branch, index) => (
                    <TableRow
                      key={`to-load-${branch.branch?._id || index}`}
                      sx={{
                        "&:hover": {
                          bgcolor: (theme) => theme.palette.action.hover
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          cursor: "pointer"
                        }}
                        onClick={() => handleBranchNameClick(branch)}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <span>
                            {branch.branch?.name || "N/A"} ({branch.branch?.code || "N/A"})
                          </span>

                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          fontWeight: "bold",
                          color: "primary.main"
                        }}
                      >
                        {branch.shipments?.length || 0}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBranchNameClick(branch);
                          }}
                        >
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow key="no-data">
                    <TableCell align="center">
                      No branch data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

        </Paper>
      </Grid>

      {/* Second Table: Loaded Shipments - Show only count */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4, boxShadow: 2, height: "100%" }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Archive sx={{ color: "secondary.main", fontSize: 28, mr: 1 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              className="text-gray-900 dark:text-white"
            >
              Loaded Shipments
            </Typography>
          </Box>

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ height: "200px" }}
          >
            {isLoadedCountLoading ? (
              <Box display="flex" flexDirection="column" alignItems="center">
                <CircularProgress size={32} sx={{ mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Loading count...
                </Typography>
              </Box>
            ) : (
              <Box textAlign="center">
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  color="primary.main"
                  sx={{ fontSize: { xs: "2.5rem", sm: "3rem" } }}
                >
                  {loadedShipmentsCount}
                </Typography>
                <Typography variant="body1" color="textSecondary" mt={1}>
                  Shipments Loaded
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>

      {/* Shipments List Modal */}
      <ShipmentsListModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        shipments={selectedShipments}
        isLoading={false}
        error={null}
        branchName={selectedBranch?.branch?.code || ""}
        branchType="loading"
      />
    </Grid>
  );
}

export default ToLoadBranchPanel;